// Color scheme of the UI (NFR-06, E-34). The account's preference comes with GET /auth/me; this
// browser caches it so public/theme-init.js can apply it before the first render.
import type { ThemePreference } from '@pm-tool/shared';

export type ResolvedTheme = 'light' | 'dark';

/** Also read by public/theme-init.js. */
const THEME_KEY = 'pm-tool.theme';

/**
 * Preference in effect: always light while the feature is off; otherwise the account's choice,
 * before it is known (loading, login page) the one cached in this browser.
 */
export function effectivePreference(options: {
  /** Feature flag `dark_mode`; undefined while the flags load. */
  enabled: boolean | undefined;
  account: ThemePreference | undefined;
  cached: ThemePreference | null;
}): ThemePreference {
  if (options.enabled === false) return 'light';
  return options.account ?? options.cached ?? 'light';
}

export function resolveTheme(preference: ThemePreference, systemDark: boolean): ResolvedTheme {
  if (preference === 'system') return systemDark ? 'dark' : 'light';
  return preference;
}

export function applyTheme(theme: ResolvedTheme) {
  const root = document.documentElement;
  root.classList.toggle('dark', theme === 'dark');
  // Native controls and scrollbars follow the scheme too.
  root.style.colorScheme = theme;
}

export function cachedPreference(): ThemePreference | null {
  try {
    const value = window.localStorage.getItem(THEME_KEY);
    return value === 'system' || value === 'light' || value === 'dark' ? value : null;
  } catch {
    return null;
  }
}

export function cachePreference(preference: ThemePreference | null) {
  try {
    if (preference === null) window.localStorage.removeItem(THEME_KEY);
    else window.localStorage.setItem(THEME_KEY, preference);
  } catch {
    // not cached: the first render is light until the account's preference is loaded
  }
}
